import {
  AlertTriangle,
  Bell,
  Clock,
  FileInput,
  Inbox,
  Mail,
  MessageSquare,
  type LucideIcon,
} from 'lucide-react';
import { formatPhoneNumber } from '@/utils/phone';

interface NotificationDisplay {
  title: string;
  subtitle: string | null;
  icon: LucideIcon;
}

function readString(
  data: Record<string, unknown> | null,
  key: string
): string | null {
  const value = data?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

/** "patientName · clinicName" for the fax-escalation notifications. */
function faxEscalationSubtitle(
  data: Record<string, unknown> | null
): string | null {
  const parts = [
    // The lookup heard a disconnected-number recording: fix the phone first.
    data?.phoneNumberInvalid === true ? 'Clinic phone number is wrong' : null,
    readString(data, 'patientName'),
    readString(data, 'clinicName'),
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * First name only — used for the acting admin ("by Grant"), not the patient
 * (whose full name we keep, e.g. "marybel estrada · by Grant").
 */
function firstNameOnly(name: string | null): string | null {
  if (!name) return null;
  return name.trim().split(/\s+/)[0] || null;
}

/**
 * Maps a notification (type + data payload) to its display title, subtitle, and
 * icon. Unknown types fall back to a generic bell so a newly-added trigger still
 * renders sensibly before this map is updated.
 */
export function notificationDisplay(
  type: string,
  data: Record<string, unknown> | null
): NotificationDisplay {
  switch (type) {
    case 'FAX_LINKED': {
      const patientName = readString(data, 'patientName');
      const actorName = firstNameOnly(readString(data, 'actorName'));
      const parts = [];
      if (patientName) parts.push(patientName);
      if (actorName) parts.push(`by ${actorName}`);
      return {
        title: 'New fax linked',
        subtitle: parts.length > 0 ? parts.join(' · ') : null,
        icon: FileInput,
      };
    }
    case 'WORKFLOW_MESSAGE': {
      const senderName = readString(data, 'senderName');
      return {
        title: 'New message',
        subtitle: senderName ? `from ${senderName}` : null,
        icon: MessageSquare,
      };
    }
    case 'FAX_NUMBER_AWAITED':
      return {
        title: 'Awaiting clinic fax number',
        subtitle: faxEscalationSubtitle(data),
        icon: Clock,
      };
    case 'FAX_NUMBER_UNRESOLVED':
      return {
        title: 'Fax number never found — needs manual handling',
        subtitle: faxEscalationSubtitle(data),
        icon: AlertTriangle,
      };
    case 'FAX_FAILED':
      return {
        title: 'Fax failed',
        subtitle: faxEscalationSubtitle(data),
        icon: AlertTriangle,
      };
    case 'PATIENT_CANCEL_REQUESTED': {
      const clinicName = readString(data, 'clinicName');
      return {
        title: 'Patient asked to cancel',
        subtitle: clinicName
          ? `Close the request and tell ${clinicName}`
          : 'Close the request',
        icon: AlertTriangle,
      };
    }
    // Order procurement: the ORDER_REQUEST fax asking the referring provider
    // for the physician's order failed permanently. The subtitle names the
    // provider. INTERNAL-only — never shown to clinics. Same data payload as
    // FAX_FAILED.
    case 'ORDER_FAX_FAILED':
      return {
        title: 'Order request fax failed',
        subtitle: faxEscalationSubtitle(data),
        icon: AlertTriangle,
      };
    // The fax telling a clinic to collect from a refunded patient failed
    // permanently. INTERNAL-only. Same data payload as FAX_FAILED.
    case 'PREPAYMENT_NOTICE_FAX_FAILED':
      return {
        title: 'Collect-payment notice fax failed',
        subtitle: faxEscalationSubtitle(data),
        icon: AlertTriangle,
      };
    case 'INBOUND_FAX_RECEIVED': {
      const senderName = readString(data, 'senderName');
      const senderFaxNumber = readString(data, 'senderFaxNumber');
      const from =
        senderName ||
        (senderFaxNumber
          ? formatPhoneNumber(senderFaxNumber) || senderFaxNumber
          : null);
      return {
        title: 'New inbound fax',
        subtitle: from ? `from ${from}` : null,
        icon: Inbox,
      };
    }
    case 'INBOUND_EMAIL': {
      const patientName = readString(data, 'patientName');
      const from = readString(data, 'from');
      const parts = [];
      if (patientName) parts.push(patientName);
      if (from) parts.push(`from ${from}`);
      return {
        title: 'New inbound email',
        subtitle: parts.length > 0 ? parts.join(' · ') : null,
        icon: Mail,
      };
    }
    default:
      return { title: type, subtitle: null, icon: Bell };
  }
}

/** Compact relative time, e.g. "just now", "5m ago", "3h ago", "2d ago". */
export function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.round(days / 7)}w ago`;
}
